const factory = (Model, Relation, Memoize, CurrencyCollection, $filter, PersonCollection, _) => {
  class Client extends Model {
    $assignedPersons() {
      const persons = new PersonCollection(this.id);

      if (persons.$lastUpdated === 0) {
        const assignedPersonsData = _(this.assignedPersons)
          .values()
          .map(_.values)
          .flatten()
          .map(
            _.partialRight(_.pick, ['id', 'fullName', 'firstName', 'lastName', 'email', 'phone']),
          )
          .value();

        persons.$set(assignedPersonsData);
      }

      return persons;
    }

    $serialize(data) {
      const newData = angular.copy(data);
      if (data.registrationDate) {
        newData.registrationDate = new Date(data.registrationDate);
      }
      if (data?.more?.integrationSettings?.initialSyncDate) {
        const utcDate = new Date(data.more.integrationSettings.initialSyncDate).toLocaleDateString(
          'en-US',
          {
            timeZone: 'UTC',
          },
        );
        newData.more.integrationSettings.initialSyncDate = new Date(utcDate);
      }

      return newData;
    }

    $deserialize(data) {
      const newData = angular.copy(data);
      if (data.registrationDate) {
        newData.registrationDate = data.registrationDate.getTime();
      }

      return newData;
    }

    currencyCode() {
      const currencies = new CurrencyCollection();
      const currentCurrency = currencies.$findById(this.currencyId);
      return currentCurrency.code;
    }

    trackConversion() {
      return this.$apiCall({
        method: 'PUT',
        url: `client.json/${this.id}/track_conversion`,
      });
    }

    changeCountry(countryCode) {
      return this.$apiCall({
        method: 'PUT',
        url: `client.json/${this.id}/country/${countryCode}`,
      })
        .then(() => this.$fetch())
        .catch(() => {
          throw new Error(`Could not change country to ${countryCode} for client ${this.id}`);
        });
    }

    setInboxState(inboxState) {
      return this.$apiCall({
        method: 'PUT',
        url: `client.json/${this.id}/inbox_state/${inboxState}`,
      });
    }

    getInitials(name) {
      return name != null
        ? name
            .split(' ')
            .map(x => x.slice(0, 1))
            .join('')
        : undefined;
    }
  }

  Object.assign(Client.prototype, {
    $resource: 'client',
    id: null,
    firstName: null,
    lastName: null,
    fullName: null,
    subscriptionPlan: null,
    registrationDate: null,
    phone: null,
    countryCode: 'US',
    email: null,
    company: null,
    companyEmail: null,
    address: {
      shippingAddressId: null,
      businessAddressId: null,
    },
    roles: ['CLIENT'],
    inboxState: 'PROSPECTIVE',
    currencyId: '2', // USD
    billing: {
      billingAccountCode: null,
    },
    assignedBookkeeper: null,
    delegatedBookkeeper: null,
    assignedSalesperson: null,
    shoeboxed: {
      showShoeboxedIntegration: null,
      shoeboxedClient: null,
    },
    notes: null,
    onHoldReason: null,
    taxAccountants: null, // fetched with getTaxAccountant
    more: {},
    exchangeRates: Relation('ExchangeRateCollection', 'currencyId'),
    ledgers: Relation('LedgerCollection', 'id'),
    shippingAddress: Relation('Address', 'address.shippingAddressId'),
    businessAddress: Relation('Address', 'address.businessAddressId'),
    $subscriptionPlan: Relation('SubscriptionPlan', 'subscriptionPlan'),
    /* eslint-disable new-cap, func-names */
    getMetadataEarliest: Memoize(function () {
      return this.$apiCall({
        method: 'GET',
        url: `client.json/${this.id}/metadata_earliest`,
      }).then(res => res.data);
    }),
  });

  return Client;
};

const moduleDeps = [
  require('data/util').name,
  require('data/collection/currency').name,
  require('data/collection/exchangeRate').name,
  require('data/collection/subscriptionPlan').name,
  require('data/collection/person').name,
];

module.exports = angular.module('bench.data.model.client', moduleDeps).factory('Client', factory);
